import * as React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";
import type { Point } from "@/lib/schedule";

/**
 * A panel that hangs off the thing you clicked.
 *
 * ── Why not `Modal` ────────────────────────────────────────────────────────
 *
 * A centred dialog answers a question that arrived from nowhere. A click on a
 * chip in a calendar is not that: it means "this one", and the thing it means
 * is a rectangle on screen with a position that is half the information. A
 * dialog throws that away, dims the week you were reading it against, and makes
 * the reader find their place again on dismiss. Every calendar worth copying
 * hangs the detail off the event instead.
 *
 * ── Why not `usePopover` ───────────────────────────────────────────────────
 *
 * That hook owns its own trigger - a `<button>` it renders the panel beside and
 * returns focus to. Here the anchor is whatever was clicked: a chip, an empty
 * hour cell, a toolbar button, a table row. So the anchor arrives as an element
 * and the open state lives with the page, which is also what lets one panel
 * serve four different origins. The hard parts below are the same ones, and
 * they are the same because each has the same specific failure behind it.
 *
 * ── The top layer is not a nicety here ─────────────────────────────────────
 *
 * The schedule is `overflow: hidden` (it is a fixed-height page) and the grid
 * inside it is too, so an absolutely positioned panel is clipped at the grid's
 * edge - which is exactly where an afternoon appointment on Sunday sits.
 * `popover="manual"` plus `showPopover()` is the way into the top layer that
 * does not also make the page inert, the same route the toaster takes. `manual`
 * rather than `auto`, or the panel would light-dismiss itself on the very
 * pointerdown that opened it.
 *
 * ── It moves between appointments rather than blinking between them ────────
 *
 * **The outside-dismissal is on `click`, in the capture phase, and that is the
 * whole trick.** On `pointerdown` it was a separate event from the `click` that
 * opened the next appointment, so React committed a closed panel and then an
 * open one and the reader saw the card go and come back. Both handlers now run
 * inside one event dispatch - ours first, because `document` is above React's
 * root in the capture path - so `open: false` and `open: true` land in the same
 * batch and there is no committed render in between. The panel never closes; it
 * is handed a different anchor and slides to it.
 *
 * Capture also replaced `swallowNextClick`. A dirty form still stops to ask, and
 * `stopPropagation()` on the very event that asked is what keeps the click from
 * reaching the chip underneath - deterministic, where a listener racing the next
 * click was not.
 *
 * The slide is confined to that case. `follow` re-places on every scroll frame,
 * and a transition on those coordinates would smear a panel that is supposed to
 * be pinned to a chip, so `moving` is set only when the panel was already open
 * and cleared once the move has landed.
 *
 * ── Dismissal is a request, not an event ───────────────────────────────────
 *
 * `onDismiss` is asked; it is not told. A read view closes on the spot and a
 * form with unsaved edits stops to ask, and the difference belongs to whoever
 * owns the content rather than to this. Which is also why a pointerdown inside
 * an open `<dialog>` is ignored: the confirmation this panel's owner opens is
 * technically "outside" it, and without that guard, answering the question
 * asks it again.
 */
/** Long enough to read as a fade, short enough that a real close feels instant. */
const LEAVE_MS = 120;

/**
 * A hair longer than the `top`/`left` transition on `.calpop--moving`, which
 * runs at `--duration-fast`. The class has to outlast the movement it drives.
 */
const MOVE_MS = 150;

/** Matches the `pop-in` on `.calpop--entering`, also `--duration-fast`. */
const ENTER_MS = 130;

export function CalendarPopover({
  open,
  anchor,
  point = null,
  title,
  size = "sm",
  onDismiss,
  action,
  footer,
  children,
}: {
  open: boolean;
  /** The element the panel hangs off. Re-measured, so it may move or resize. */
  anchor: HTMLElement | null;
  /**
   * Where the pointer was when it opened. Used instead of the anchor's own edges
   * when the anchor is a full-width row, where "beside it" is off the screen.
   */
  point?: Point | null;
  title: string;
  size?: "sm" | "md";
  /**
   * Asked to close. `viaKeyboard` when the reader pressed Escape.
   *
   * Return `true` to say the panel is *not* closing yet because you are asking
   * the reader something. The click that triggered the dismissal is then
   * stopped where it stands, so it never reaches the chip underneath.
   */
  onDismiss: (viaKeyboard: boolean) => boolean | void;
  /** Beside the close button, on the title's own line. */
  action?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
}) {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState<React.CSSProperties | null>(null);
  /** Closing, but still painted. See the note above. */
  const [leaving, setLeaving] = React.useState(false);
  /** Sliding from one anchor to another. */
  const [moving, setMoving] = React.useState(false);
  /**
   * Playing its entrance.
   *
   * A state that clears itself rather than an animation left on `--placed` for
   * the panel's whole life. That version replayed `pop-in` every time another
   * class stopped suppressing it - which is once per appointment switch, and is
   * the second of the two flashes this used to have.
   */
  const [entering, setEntering] = React.useState(false);
  const titleId = React.useId();

  // Held in a ref so the placement and dismissal effects do not re-run every
  // time the owner re-renders with a new closure.
  const dismissRef = React.useRef(onDismiss);
  dismissRef.current = onDismiss;

  React.useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    if (!open) {
      if (!panel.matches(":popover-open")) return;
      // Held in the top layer while it fades, and torn down afterwards. The
      // cleanup below cancels this, which is what turns "close A, open B" into
      // a move.
      setLeaving(true);
      const timer = window.setTimeout(() => {
        if (panel.matches(":popover-open")) panel.hidePopover();
        setLeaving(false);
        setMoving(false);
        setEntering(false);
        setPosition(null);
      }, LEAVE_MS);
      return () => window.clearTimeout(timer);
    }

    setLeaving(false);

    // Promoted before the first measurement, never after: a `popover="manual"`
    // element is UA-styled `display: none` until shown, so `offsetHeight` would
    // read 0 and every fit decision below would be made against a zero-height
    // panel.
    const reused = panel.matches(":popover-open");
    if (!reused) panel.showPopover();
    // Both are batched with the `place()` below into one commit, so the class
    // and the coordinates it drives arrive together.
    setEntering(!reused);
    setMoving(reused);

    function place() {
      const el = panelRef.current;
      if (!el || !anchor) return;
      setPosition(
        placeBeside(anchor.getBoundingClientRect(), point, el.offsetWidth, el.offsetHeight)
      );
    }

    place();

    // The move above is over by now, and `follow` below must not inherit its
    // transition - a panel that eases toward a chip while the grid scrolls is a
    // panel that is never quite on it.
    const settle = window.setTimeout(() => setMoving(false), MOVE_MS);
    const entered = window.setTimeout(() => setEntering(false), ENTER_MS);

    /**
     * Followed rather than dismissed on scroll.
     *
     * `usePopover` closes instead, and for a menu that is right - it is a list
     * of actions you either take or abandon. This one can hold a form somebody
     * is halfway through, and a panel that deletes an address you were typing
     * because the grid scrolled under it is indefensible. Re-placing costs one
     * rect read per frame, throttled to the frame the browser was going to
     * paint anyway.
     *
     * `capture`, because scroll events do not bubble and the scroll that
     * actually moves the anchor is the one inside the week grid.
     */
    let frame = 0;
    const follow = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        place();
      });
    };

    /**
     * And re-placed when the panel's own height changes, which is the case that
     * was broken.
     *
     * The panel opens on the read view, roughly 300px. Pressing Edit swaps in
     * the form, which is twice that - and the top edge was computed once, for
     * the short one, so the tall one ran off the bottom of the window with its
     * footer and its Save button below the fold. Nothing scrolls or resizes when
     * that happens, so neither listener above fires. `top` is clamped against
     * the measured height, so re-measuring is the whole fix.
     *
     * No feedback loop: `place` writes `top`, `left` and a `max-height` that
     * depends only on the viewport, and none of the three changes the height
     * this observer is watching.
     */
    const resize = new ResizeObserver(follow);
    resize.observe(panel);

    window.addEventListener("scroll", follow, true);
    window.addEventListener("resize", follow);
    return () => {
      window.clearTimeout(settle);
      window.clearTimeout(entered);
      if (frame) cancelAnimationFrame(frame);
      resize.disconnect();
      window.removeEventListener("scroll", follow, true);
      window.removeEventListener("resize", follow);
    };
  }, [open, anchor, point]);

  React.useEffect(() => {
    if (!open) return;

    /** Whether the gesture *started* inside, which a click's target cannot say. */
    let fromInside = false;

    function inside(target: EventTarget | null): boolean {
      if (!(target instanceof Node)) return false;
      if (panelRef.current?.contains(target) || anchor?.contains(target)) return true;

      // A confirmation opened by this panel's own footer is "outside" it, and so
      // is every dropdown inside it: `usePopover` portals its panel to `<body>`
      // to escape clipping, so choosing a type or a date happens on a node that
      // is nowhere near this one in the DOM. Both are `[popover]` or
      // `dialog[open]`, and neither means "dismiss".
      return target instanceof Element && Boolean(target.closest("[popover], dialog[open]"));
    }

    // Kept only to answer the question above. A drag that selects text in the
    // panel and releases over the calendar produces a click on their common
    // ancestor, which is "outside" by every test the click itself can run - and
    // closing the panel somebody was selecting from is indefensible.
    function onPointerDown(event: PointerEvent) {
      fromInside = inside(event.target);
    }

    function onClick(event: MouseEvent) {
      if (fromInside || inside(event.target)) return;

      // Held open to ask something, so the click that asked must not also reach
      // whatever is under it. Capture is what makes this reliable: React's own
      // listeners live on the root container, below `document`, so stopping the
      // event here means the chip's `onClick` never runs at all.
      if (dismissRef.current(false)) {
        event.stopPropagation();
        event.preventDefault();
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      // Only the topmost surface answers Escape. A confirmation over this panel
      // is a `<dialog>` and handles its own.
      if (event.key !== "Escape") return;
      if (document.querySelector("dialog[open]")) return;
      dismissRef.current(true);
    }

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("click", onClick, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, anchor]);

  // Focus moves into the panel so the keyboard follows the eye, and Tab from
  // here walks the panel rather than resuming from the calendar behind it. The
  // panel itself rather than its first control: landing on "Cancel appointment"
  // is one mis-hit away from a booking somebody has to reinstate.
  React.useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  return createPortal(
    <div
      ref={panelRef}
      popover="manual"
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      tabIndex={-1}
      className={cn(
        "calpop",
        `calpop--${size}`,
        position && "calpop--placed",
        entering && "calpop--entering",
        moving && "calpop--moving",
        leaving && "calpop--leaving"
      )}
      style={position ?? undefined}
    >
      <header className="calpop__header">
        <h2 id={titleId} className="calpop__title">
          {title}
        </h2>
        {/* Beside the close rather than in the footer. The footer is where the
            actions that *change the record* live, and mixing "edit this" in with
            them made a four-button bar that wrapped onto two rows in a 368px
            panel - which is how a destructive button ended up under the reader's
            cursor. */}
        <div className="calpop__tools">
          {action}
          <button
            type="button"
            onClick={() => onDismiss(true)}
            aria-label="Close"
            className="calpop__close"
          >
            <X />
          </button>
        </div>
      </header>

      <div className="calpop__body">{children}</div>

      {footer && <footer className="calpop__footer">{footer}</footer>}
    </div>,
    document.body
  );
}

/**
 * Beside the thing you clicked if it fits, otherwise wherever it does.
 *
 * Preference order is right, then left, then clamped over the top of it. Above
 * or below would be the obvious fourth option and it is wrong for a calendar: a
 * panel under a 9am appointment covers the rest of that day, which is the
 * context the reader opened it to keep.
 *
 * **"The thing you clicked" is the anchor for a chip and the pointer for a
 * row.** A chip is a small object with two free sides and the panel belongs
 * against one of them. An agenda row is the width of the window: its `right`
 * edge is already past the fold, so every row fell through to the same clamp
 * and the panel opened in one fixed place whichever chip was picked. Collapsing
 * a wide anchor to the point that was clicked restores the one property that
 * made this a popover rather than a dialog - it comes out of where you looked.
 *
 * Half the viewport is the line, and it is deliberately generous: the week
 * grid's chips are a seventh of the width and the tray's are 200px, so nothing
 * that has a genuine "beside" is caught by it.
 */
function placeBeside(
  anchor: DOMRect,
  point: Point | null,
  width: number,
  height: number
): React.CSSProperties {
  const gap = 10;
  const margin = 12;
  // Capped well under the viewport - uncapped, this read as a page of its own
  // on a tall monitor instead of a panel beside the thing you clicked.
  const maxHeight = Math.min(window.innerHeight - margin * 2, 640);

  const wide = anchor.width > window.innerWidth / 2;
  const from =
    wide && point
      ? { left: point.x, right: point.x, top: point.y }
      : { left: anchor.left, right: anchor.right, top: anchor.top };

  let left = from.right + gap;
  if (left + width > window.innerWidth - margin) {
    left = from.left - gap - width;
    if (left < margin) {
      left = clamp(from.left, margin, window.innerWidth - width - margin);
    }
  }

  // Top-aligned with the anchor, then pulled back inside the viewport. Aligning
  // the panel's centre reads better on a one-hour chip and puts a tall form
  // half off the screen on an all-morning one.
  const top = clamp(from.top, margin, Math.max(margin, window.innerHeight - height - margin));

  return { position: "fixed", top, left, maxHeight };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
